/**
 * Skin Quiz — storefront renderer + scoring.
 *
 * Reads two JSON blobs injected by sections/quiz.liquid:
 *   - [data-quiz-config]  → { questions:[{question,answers:[{answer,recommended_products:[{handle}]}]}], outcome_steps:[{title,products:[{handle}]}] }
 *   - [data-quiz-catalog] → { <handle>: { title, url, image, price, compare_at_price, available, variant_id } }
 *
 * Flow: intro → questions… → age → email → results.
 * Each question/age/email step has Previous/Next buttons; Next is disabled
 * until the step is answered (option chosen / age filled / valid email).
 *
 * Scoring (ported from the WordPress Vue component):
 *   - every chosen answer adds +1 to each product it recommends
 *   - for each outcome step, the highest-scoring product in that group wins
 */
class QuizSection extends HTMLElement {
  constructor() {
    super();

    this.config = this._readJSON('[data-quiz-config]') || {};
    this.catalog = this._readJSON('[data-quiz-catalog]') || {};
    this.questions = Array.isArray(this.config.questions)
      ? this.config.questions
      : [];
    this.outcomeSteps = Array.isArray(this.config.outcome_steps)
      ? this.config.outcome_steps
      : [];

    this.discountCode = this.dataset.discountCode || '';
    this.ageQuestion = this.dataset.ageQuestion || 'How old are you?';
    this.emailQuestion =
      this.dataset.emailQuestion || 'Lastly, what is your email?';
    this.consentLabel = this.dataset.consentLabel || '';
    this.klaviyoListId = this.dataset.klaviyoList || '';
    this.klaviyoPublicKey = this.dataset.klaviyoKey || '';
    this.region = this.dataset.region || '';

    this.answers = {}; // { questionIndex: answerIndex }
    this.age = '';
    this.email = '';
    this.consent = true;
    this.currentStep = 0; // 0 = intro
    this.steps = [];

    this._render();
    this._bindStaticEvents();
    this._collectSteps();
  }

  /* ---- Data ------------------------------------------------------------ */
  _readJSON(selector) {
    const el = this.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[quiz] invalid JSON in', selector, e);
      return null;
    }
  }

  _formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  _escape(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  _validEmail(v) {
    return typeof v === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  }

  /* ---- Render ---------------------------------------------------------- */
  _render() {
    // Progress dots — one per question plus the age + email steps.
    const progress = this.querySelector('[data-quiz-progress]');
    if (progress) {
      const dotCount = this.questions.length + 2;
      progress.innerHTML = Array.from({ length: dotCount })
        .map(() => '<span class="quiz__progress-dot"></span>')
        .join('');
    }

    // Question steps, then the age + email steps.
    const container = this.querySelector('[data-quiz-questions]');
    if (container) {
      container.innerHTML =
        this.questions.map((q, qi) => this._questionMarkup(q, qi)).join('') +
        this._ageMarkup() +
        this._emailMarkup();
    }
  }

  _navMarkup(nextLabel) {
    return `
      <div class="quiz__nav quiz__nav--split">
        <button class="quiz__nav-btn quiz__nav-btn--prev" type="button" data-quiz-back>
          <span class="quiz__nav-arrow" aria-hidden="true">&larr;</span> Previous question
        </button>
        <button class="quiz__nav-btn quiz__nav-btn--next" type="button" data-quiz-next disabled>
          ${this._escape(nextLabel)} <span class="quiz__nav-arrow" aria-hidden="true">&rarr;</span>
        </button>
      </div>`;
  }

  _questionMarkup(question, qi) {
    const answers = Array.isArray(question.answers) ? question.answers : [];
    const answersHtml = answers
      .map(
        (a, ai) =>
          `<button class="quiz__answer" type="button" data-answer-index="${ai}">${this._escape(
            a.answer
          )}</button>`
      )
      .join('');

    return `
      <div class="quiz__step" data-quiz-step="question" data-question-index="${qi}">
        <p class="quiz__question-text h4">${this._escape(question.question)}</p>
        <div class="quiz__answers">${answersHtml}</div>
        ${this._navMarkup('Next question')}
      </div>`;
  }

  _ageMarkup() {
    return `
      <div class="quiz__step" data-quiz-step="age">
        <p class="quiz__question-text h4">${this._escape(this.ageQuestion)}</p>
        <div class="quiz__field">
          <input class="quiz__input" type="number" min="1" max="120" inputmode="numeric" data-quiz-age aria-label="${this._escape(this.ageQuestion)}">
        </div>
        ${this._navMarkup('Next question')}
      </div>`;
  }

  _emailMarkup() {
    const consent = this.consentLabel
      ? `<label class="quiz__consent">
           <input type="checkbox" data-quiz-consent checked>
           <span>${this._escape(this.consentLabel)}</span>
         </label>`
      : '';
    return `
      <div class="quiz__step" data-quiz-step="email">
        <p class="quiz__question-text h4">${this._escape(this.emailQuestion)}</p>
        <div class="quiz__field">
          <input class="quiz__input" type="email" autocomplete="email" data-quiz-email aria-label="${this._escape(this.emailQuestion)}">
        </div>
        ${consent}
        ${this._navMarkup('Submit')}
      </div>`;
  }

  /* ---- Steps / navigation --------------------------------------------- */
  _collectSteps() {
    this.steps = Array.from(this.querySelectorAll('.quiz__step'));
    this.dots = Array.from(this.querySelectorAll('.quiz__progress-dot'));
  }

  _bindStaticEvents() {
    this.addEventListener('click', (e) => {
      const answerBtn = e.target.closest('.quiz__answer');
      if (answerBtn) return this._onAnswer(answerBtn);

      if (e.target.closest('[data-quiz-next]')) {
        const step = this.steps[this.currentStep];
        if (step && step.dataset.quizStep === 'email') return this._submit();
        return this._goTo(this.currentStep + 1);
      }
      if (e.target.closest('[data-quiz-back]'))
        return this._goTo(this.currentStep - 1);
      if (e.target.closest('[data-quiz-restart]')) return this._restart();
      if (e.target.closest('[data-quiz-add-bundle]')) return this._addBundle();

      const addBtn = e.target.closest('[data-quiz-add-product]');
      if (addBtn) return this._addProduct(addBtn.dataset.variantId);
    });

    // Age / email inputs + marketing consent.
    this.addEventListener('input', (e) => {
      if (e.target.matches('[data-quiz-age]')) {
        this.age = e.target.value;
        this._updateNextState();
      } else if (e.target.matches('[data-quiz-email]')) {
        this.email = e.target.value;
        this._updateNextState();
      }
    });
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-quiz-consent]')) {
        this.consent = e.target.checked;
      }
    });
  }

  _onAnswer(btn) {
    const step = btn.closest('.quiz__step');
    const qi = parseInt(step.dataset.questionIndex, 10);
    const ai = parseInt(btn.dataset.answerIndex, 10);

    step
      .querySelectorAll('.quiz__answer')
      .forEach((b) => b.classList.remove('is-selected'));
    btn.classList.add('is-selected');

    this.answers[qi] = ai;
    this._updateNextState();
  }

  /* Enable the Next/Submit button only once the current step is answered. */
  _updateNextState() {
    const step = this.steps[this.currentStep];
    if (!step) return;
    const next = step.querySelector('[data-quiz-next]');
    if (!next) return;

    let ok = true;
    switch (step.dataset.quizStep) {
      case 'question':
        ok = this.answers[parseInt(step.dataset.questionIndex, 10)] != null;
        break;
      case 'age':
        ok = String(this.age).trim() !== '';
        break;
      case 'email':
        ok = this._validEmail(this.email);
        break;
    }
    next.disabled = !ok;
  }

  _goTo(index) {
    if (index < 0 || index >= this.steps.length) return;

    this.steps[this.currentStep].classList.remove('is-active');
    this.currentStep = index;
    this.steps[this.currentStep].classList.add('is-active');

    const step = this.steps[this.currentStep];
    const type = step.dataset.quizStep;

    // Progress bar shows only while answering (questions/age/email).
    const progress = this.querySelector('[data-quiz-progress]');
    if (progress) {
      const showProgress = ['question', 'age', 'email'].includes(type);
      progress.classList.toggle('is-visible', showProgress);
    }
    this.dots.forEach((dot, i) =>
      dot.classList.toggle('is-active', i < index)
    );

    if (type === 'results') this._renderResults(step);

    this._updateNextState();
    this._scrollToTop();
  }

  /* Scroll the quiz into view, offset by the sticky header so the progress
     bar at the top of the section isn't hidden underneath it. */
  _scrollToTop() {
    const header = document.querySelector('.wt-header');
    const headerH = header ? header.getBoundingClientRect().height : 0;
    const top =
      this.getBoundingClientRect().top + window.pageYOffset - headerH - 16;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  _restart() {
    this.answers = {};
    this.age = '';
    this.email = '';
    this.consent = true;
    this.querySelectorAll('.quiz__answer').forEach((b) =>
      b.classList.remove('is-selected')
    );
    this.querySelectorAll('[data-quiz-age], [data-quiz-email]').forEach(
      (i) => (i.value = '')
    );
    this._goTo(0);
  }

  /* ---- Scoring (ported from WP Vue) ----------------------------------- */
  _scoreByHandle() {
    const scores = {};
    Object.entries(this.answers).forEach(([qi, ai]) => {
      const question = this.questions[qi];
      const answer = question && question.answers[ai];
      if (!answer || !Array.isArray(answer.recommended_products)) return;
      answer.recommended_products.forEach((p) => {
        if (!p || !p.handle) return;
        scores[p.handle] = (scores[p.handle] || 0) + 1;
      });
    });
    return scores;
  }

  _productOutcomes() {
    const scores = this._scoreByHandle();
    const outcomes = [];
    this.outcomeSteps.forEach((step) => {
      if (!Array.isArray(step.products)) return;
      const ranked = step.products
        .map((p) => ({ handle: p.handle, score: scores[p.handle] || 0 }))
        .filter((p) => p.score > 0)
        .sort((a, b) => b.score - a.score);
      const best = ranked[0];
      if (best && this.catalog[best.handle]) {
        outcomes.push({ handle: best.handle, ...this.catalog[best.handle] });
      }
    });
    return outcomes;
  }

  /* ---- Submission ----------------------------------------------------- */
  _submit() {
    if (!this._validEmail(this.email)) return;

    // Subscribe to the Klaviyo list when the buyer has consented.
    if (this.klaviyoListId && this.klaviyoPublicKey && this.consent) {
      this._klaviyoSubscribe();
    }

    this._goTo(this.currentStep + 1); // → results
  }

  /* Build the custom profile properties from the quiz answers. */
  _quizProperties() {
    const props = { region: this.region };
    if (String(this.age).trim()) props.age = String(this.age).trim();

    this.questions.forEach((q, qi) => {
      const ai = this.answers[qi];
      if (ai != null && q.answers[ai]) {
        props[`Quiz: ${q.question}`] = q.answers[ai].answer;
      }
    });

    const products = this._productOutcomes().map((p) => p.title);
    if (products.length) props['Quiz recommendations'] = products.join(', ');
    return props;
  }

  /* Subscribe the buyer to a Klaviyo list via the Client API. Needs the
     public API key (company_id) + list ID; runs fire-and-forget. Quiz answers
     and recommended products are stored as custom profile properties. */
  _klaviyoSubscribe() {
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Skin Quiz',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: this.email.trim(),
                properties: this._quizProperties(),
              },
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: this.klaviyoListId } },
        },
      },
    };

    fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(
        this.klaviyoPublicKey
      )}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-10-15',
        },
        body: JSON.stringify(payload),
      }
    ).catch((err) => console.error('[quiz] klaviyo subscribe failed', err));
  }

  /* ---- Results -------------------------------------------------------- */
  _renderResults(container) {
    const outcomes = this._productOutcomes();

    if (outcomes.length === 0) {
      container.innerHTML = `
        <div class="quiz__result">
          <h3 class="quiz__result-heading h3">No perfect match found</h3>
          <p class="quiz__result-body">Try retaking the quiz with different answers.</p>
          <div class="quiz__nav">
            <button class="button button--secondary" type="button" data-quiz-restart>Retake quiz</button>
          </div>
        </div>`;
      return;
    }

    const total = outcomes.reduce((sum, p) => sum + (p.price || 0), 0);

    const cards = outcomes
      .map(
        (p) => `
        <div class="quiz__result-card">
          <a href="${p.url}" class="quiz__result-card-image">
            ${p.image ? `<img src="${p.image}" alt="${this._escape(p.title)}" loading="lazy">` : ''}
          </a>
          <div class="quiz__result-card-info">
            <a href="${p.url}"><p class="quiz__result-card-title">${this._escape(p.title)}</p></a>
            <p class="quiz__result-card-price">${this._formatMoney(p.price)}</p>
            <button class="button button--secondary" type="button" data-quiz-add-product data-variant-id="${p.variant_id}" ${p.available ? '' : 'disabled'}>
              ${p.available ? 'Add to cart' : 'Sold out'}
            </button>
          </div>
        </div>`
      )
      .join('');

    container.innerHTML = `
      <h3 class="quiz__result-heading h3">Your personalised routine</h3>
      <div class="quiz__results-grid">${cards}</div>
      <div class="quiz__result-total">
        <span>Bundle total</span>
        <strong>${this._formatMoney(total)}</strong>
      </div>
      <div class="quiz__nav">
        <button class="button" type="button" data-quiz-add-bundle data-variants="${outcomes
          .map((p) => p.variant_id)
          .join(',')}">Add all to cart</button>
        <button class="button button--secondary" type="button" data-quiz-restart>Retake quiz</button>
      </div>`;
  }

  /* ---- Cart ------------------------------------------------------------ */
  async _addProduct(variantId) {
    await this._cartAdd([{ id: variantId, quantity: 1 }]);
  }

  async _addBundle() {
    const btn = this.querySelector('[data-quiz-add-bundle]');
    const variants = (btn?.dataset.variants || '')
      .split(',')
      .filter(Boolean)
      .map((id) => ({ id, quantity: 1 }));
    if (variants.length === 0) return;
    await this._cartAdd(variants);
  }

  async _cartAdd(items) {
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (this.discountCode) {
        window.location.href = `/discount/${encodeURIComponent(
          this.discountCode
        )}?redirect=/cart`;
      } else {
        window.location.href = '/cart';
      }
    } catch (e) {
      console.error('[quiz] cart add failed', e);
    }
  }
}

customElements.define('quiz-section', QuizSection);
